/**
 * Service to check Program
 */

import ApiService from "@/gapp-components/services/api.service";
import moment from "moment";

let instanceRouter = null;
const KMC_ADMIN_PARTICIPANT_TYPES = ["100", "140", "150", "160", "170"];

export const SSN_ALREADY_REGISTERED_MESSAGE =
  "This Social Security Number is already registered in the program. If you believe this is an error or need assistance accessing your existing account, please contact Customer Support at 1-888-399-0681, (Monday-Friday, 8:30am to 5:00pm CT)";

const KawasakiService = {
  init(router) {
    instanceRouter = router;
  },

  isKmcAdmin() {
    const participantTypeKey =
      instanceRouter.app.$store.getters.selectedParticipant?.participantType?.participantTypeKey;
    return KMC_ADMIN_PARTICIPANT_TYPES.includes(participantTypeKey);
  },

  isRegionalSalesDirector() {
    let participant = instanceRouter.app.$store.getters.selectedParticipant;
    return participant && participant.participantType && participant.participantType.participantTypeKey == "210";
  },

  isDistrictManager() {
    let participant = instanceRouter.app.$store.getters.selectedParticipant;
    return participant && participant.participantType && participant.participantType.participantTypeKey == "220";
  },

  isStaffOrEmployee() {
    let participant = instanceRouter.app.$store.getters.selectedParticipant;
    return participant && participant.participantType && participant.participantType.participantTypeKey == "230";
  },

  isDealerPrincipalOrDealership() {
    let participant = instanceRouter.app.$store.getters.selectedParticipant;
    return participant && participant.participantType && participant.participantType.participantTypeKey == "240";
  },

  /** Program Registration Date - this field includes the registration date for both Racer or Sale program */
  isActiveInProgram() {
    let participant = instanceRouter.app.$store.getters.selectedParticipant;
    return (
      participant &&
      participant.customFieldValue1 &&
      moment(participant.customFieldValue1).isSameOrBefore(moment()) &&
      participant.customFieldValue2 &&
      participant.customFieldValue2 == "true"
    );
  },

  /** Racer Methods */

  isRacer() {
    let participant = instanceRouter.app.$store.getters.selectedParticipant;
    return participant && participant.participantType && participant.participantType.participantTypeKey == "400";
  },

  isRacerAdmin() {
    return instanceRouter.app.$store.getters.selectedParticipant?.participantType?.participantTypeKey === "170";
  },

  hasActiveRacerParticipant() {
    let availableParticipants = instanceRouter.app.$store.getters.availableParticipants;
    if (availableParticipants) {
      let racer = availableParticipants.find(
        p => p.participantType.participantTypeKey == "400" && p.status.name == "ENR"
      );
      return racer && racer.id;
    }
    return false;
  },

  /** Salesperson Methods */

  isSalesperson() {
    let participant = instanceRouter.app.$store.getters.selectedParticipant;
    return participant && participant.participantType && participant.participantType.participantTypeKey == "300";
  },

  hasActiveSalespersonParticipant() {
    let availableParticipants = instanceRouter.app.$store.getters.availableParticipants;
    if (availableParticipants) {
      let salesperson = availableParticipants.find(
        p => p.participantType.participantTypeKey == "300" && p.status.name == "ENR"
      );
      return salesperson && salesperson.id;
    }
    return false;
  },

  isParentConsentInitialized() {
    const participant = instanceRouter.app.$store.getters.selectedParticipant;

    return Boolean(participant.user?.externalConsentIdentifier);
  },

  async hasParentConsents() {
    let participant = instanceRouter.app.$store.getters.selectedParticipant;

    if (!this.isUnder18(participant.user.birthDate)) return true;

    if (!this.isParentConsentInitialized()) return false;

    return this.isConsentApprovedState(participant);
  },

  async isConsentApprovedState(participant) {
    try {
      let { data } = await ApiService.get(`/api/privo/consent/status/${participant.id}`);

      if (!data) return false;

      return (
        data?.consentRequestState?.trim().toLowerCase() === "approved" &&
        data?.featureRewardState?.trim().toLowerCase() === "on"
      );
    } catch (error) {
      console.log("Error fetching parental consent status: ", error);
      return false;
    }
  },

  isMinor() {
    return this.isRacer() && this.isUnder18(instanceRouter.app.$store.getters.selectedParticipant.user.birthDate);
  },

  isUnder18(date) {
    if (!date) {
      return false;
    }
    const birthDate = moment(date);
    const today = moment();
    const age = today.diff(birthDate, "years");
    return age < 18;
  },

  async saveTin(tin) {
    await ApiService.post("/api/users/setTin", { tin });
  },

  getDuplicateSsnMessage(error) {
    return this.isDuplicateTinError(error) ? SSN_ALREADY_REGISTERED_MESSAGE : null;
  },

  scrollToSsnSection() {
    document.getElementById("ssnSection")?.scrollIntoView({ behavior: "smooth", block: "center" });
  },

  isDuplicateTinError(error) {
    let response = error && error.response ? error.response : null;
    if (!response) {
      return false;
    }

    let alreadyExists = this.getApiErrorMessages(response.data).some(message =>
      /already\s+(exists|registered|in use)/i.test(message)
    );

    return alreadyExists || response.status == 409;
  },

  getApiErrorMessages(data) {
    if (!data) {
      return [];
    }

    let messages = [data.errorMessage, data.detail, data.message];

    if (Array.isArray(data.errors)) {
      messages = messages.concat(data.errors.map(e => e && e.message));
    } else if (data.errors && data.errors.fields) {
      messages = messages.concat(Object.values(data.errors.fields));
    }

    if (data.properties && data.properties.errors && data.properties.errors.fields) {
      messages = messages.concat(Object.values(data.properties.errors.fields));
    }

    return messages.filter(message => typeof message === "string");
  },

  async saveParticipantVehicles(vehicles) {
    const newVehicles = vehicles.existing.filter(v => !v.id);
    let postPromises = newVehicles.map(vehicle => {
      const productRegistrationForm = {
        productRegistrationItems: [
          {
            product: vehicle.product,
            serialNumber: vehicle.serialNumber
          }
        ]
      };
      ApiService.post("/api/productRegistrations/submit", productRegistrationForm);
    });
    const deleteRegistrationPromises = vehicles.delete.map(vehicle =>
      ApiService.delete(`/api/productRegistrations/${vehicle.registrationId}/delete`)
    );
    await Promise.all([...deleteRegistrationPromises, ...postPromises]);
  },

  async createReloadableCard(awardVehicleKey) {
    // Don't issue cards for non-racer or non-salesperson participants
    if (!this.isRacer() && !this.isSalesperson()) {
      console.log("Not a racer or salesperson. No reloadable card will be issued.");
      return;
    }

    let participant = instanceRouter.app.$store.getters.selectedParticipant;

    const avs = await instanceRouter.app.$awardvehicle.getAvailableAwardVehicles(participant);
    let awardVehicle = avs.find(av => av.awardVehicleKey === awardVehicleKey);

    if (!awardVehicle) {
      throw new Error("Award Vehicle not found.");
    }

    const cardResponse = await ApiService.post(
      `/api/prepaidTechnologiesCards/awardVehicle/${awardVehicle.id}/card/create`
    );

    return cardResponse.data;
  },

  async saveParticipantDealers(dealers) {
    let participant = instanceRouter.app.$store.getters.selectedParticipant;
    const deletePromises = dealers.delete.map(org => {
      if (org.relationshipId && org.relationshipId > 0) {
        return ApiService.delete(`/api/relatedOrganizations/${org.relationshipId}`);
      }
      return Promise.resolve();
    });
    await Promise.all([...deletePromises]);

    await this.updatePrimaryDealer(dealers.existing);

    const postPromises = dealers.existing
      .filter(dealer => !dealer.relationshipId)
      .map(async dealer => {
        const payload = {
          relatedOrganization: ApiService.getSelfUrl("organization", dealer),
          participant: ApiService.getSelfUrl("participant", participant),
          description: dealer.jobTitle
        };
        const { data } = await ApiService.post("/api/relatedOrganizations", payload);
        dealer.relationshipId = data.id;
        dealer.createdDate = data.createdDate;
        dealer.dirty = false;
        return data;
      });

    await Promise.all([...postPromises]);
  },

  async updatePrimaryDealer(dealers) {
    let participant = instanceRouter.app.$store.getters.selectedParticipant;
    let primaryDealer = dealers.find(dealer => dealer.isPrimary);
    primaryDealer ??= dealers[0];
    if (primaryDealer) {
      const participantForm = {
        customFieldValue16: primaryDealer.jobTitle,
        organization: ApiService.getSelfUrl("organization", primaryDealer)
      };
      await ApiService.patch(`/api/participants/${participant.id}`, participantForm);
    } else {
      const participantForm = {
        customFieldValue16: "",
        organization: null
      };
      await ApiService.patch(`/api/participants/${participant.id}`, participantForm);
    }
  },

  async getParticipantTypeForSalespersonAndRacer() {
    const { data } = await ApiService.post("/api/participantTypes/search?size=1", { participantTypeKey: 350 });
    return data.content[0];
  },

  async updateParticipantType(participantType) {
    let participant = instanceRouter.app.$store.getters.selectedParticipant;
    await ApiService.patch("/api/participants/" + participant.id, {
      participantType: ApiService.getSelfUrl("participantType", participantType)
    });
  },

  async registerSalespersonAndIssueCard(organizations, participantTypeKey) {
    try {
      await this.saveParticipantDealers(organizations);
      const joinForm = {
        customFieldValue1: moment(),
        customFieldValue2: true,
        statusName: participantTypeKey !== "300" ? "PRE" : null
      };
      const updatedParticipant = await this.joinSalespersonProgram(joinForm);
      const { data } = await ApiService.post("/api/participantTypes/search?size=1", {
        participantTypeKey
      });
      const participantType = data.content[0];
      await this.updateParticipantType(participantType);
      const emailTemplateForm = this.buildSalesEmailTemplate(updatedParticipant);
      try {
        await ApiService.post("/api/emailTemplates/sendToParticipant", emailTemplateForm);
      } catch (err) {
        console.error("Error sending salesperson join email:", err);
      }
      try {
        await this.createReloadableCard("SALESPERSON_PAYOUTS");
      } catch (cardError) {
        console.error("Error issuing card:", cardError);
        throw {
          msg: "CARD_ERROR",
          participant: updatedParticipant,
          originalError: cardError
        };
      }
      return {
        success: true,
        participant: updatedParticipant
      };
    } catch (error) {
      console.error("Error registering salesperson:", error);
      throw error;
    }
  },

  async updateSalespersonDealers(organizations, participantTypeKey) {
    try {
      await this.saveParticipantDealers(organizations);
      const { data } = await ApiService.post("/api/participantTypes/search?size=1", {
        participantTypeKey
      });
      const participantType = data.content[0];
      await this.updateParticipantType(participantType);
      const { data: updatedParticipant } = await ApiService.get("/api/participants/bySelectedParticipant");
      await instanceRouter.app.$auth.storeSelectParticipant(updatedParticipant);
      return { success: true, participant: updatedParticipant };
    } catch (error) {
      console.error("Error updating salesperson dealers:", error);
      throw error;
    }
  },

  buildSalesEmailTemplate(participant) {
    const emailTemplateForm = { participant: { id: participant.id } };

    if (participant?.status?.name === "ENR") {
      emailTemplateForm.emailTemplateKey = "SALES_REWARDS_JOIN";
    } else if (participant?.status?.name === "PRE") {
      emailTemplateForm.emailTemplateKey = "SALES_REWARDS_PENDING";
    }

    return emailTemplateForm;
  }
};

export default KawasakiService;
